/**
 * Risk Assessment Program service — rolled-out instances of risk assessment
 * templates. Mirrors the shape of controlTestProgramService.
 */
import apiClient from './apiService';
import { GeographySelection } from '../components/common/GeographyPicker';

export interface RiskAssessmentRolloutPayload {
    program_name?: string;
    maker_id: number;
    checker_id: number;
    geography: GeographySelection;
    start_date: string;
    end_date: string;
}

export interface RiskAssessmentProgram {
    id: number;
    entity_id: number;
    template_id?: number | null;
    program_code?: string | null;
    program_name: string;
    program_description?: string | null;
    maker_id?: number | null;
    checker_id?: number | null;
    location_id?: number | null;
    start_date?: string | null;
    end_date?: string | null;
    due_date?: string | null;
    status?: string | null;
    is_active: number;
    is_approved?: number;
    rejection_reason?: string | null;
    template_code?: string | null;
    template_name?: string | null;
    maker_name?: string | null;
    checker_name?: string | null;
    location_name?: string | null;
    risk_count?: number;
    completed_count?: number;
}

export interface ProgramImplScoreRow {
    impl_score_id: number;
    control_score_id: number;
    implementation_id: number;
    result?: 'Pass' | 'Fail' | 'NA' | null;
    remarks?: string | null;
    is_approved?: number;
    sequence_number?: number | null;
    implementation_type?: string | null;
    target_id?: number | null;
    target_label?: string | null;
    target_subtitle?: string | null;
    explanation?: string | null;
}

export interface ProgramControlScoreRow {
    control_score_id: number;
    risk_score_id: number;
    risk_id: number;
    control_id: number;
    control_code?: string;
    control_name?: string;
    control_type?: string | null;
    existence_score?: number | null;
    effectiveness_score?: number | null;
    remarks?: string | null;
    is_approved?: number;
    implementations: ProgramImplScoreRow[];
}

export interface ProgramRiskRow {
    score_id: number;
    risk_id: number;
    risk_code?: string;
    risk_name?: string;
    description?: string;
    likelihood?: number | null;
    severity?: number | null;
    inherent_risk?: number | null;
    control_existence?: number | null;
    control_effectiveness?: number | null;
    control_risk?: number | null;
    residual_risk?: number | null;
    remarks?: string | null;
    is_approved?: number;
    controls: ProgramControlScoreRow[];
}

export interface RiskAssessmentProgramDetail extends RiskAssessmentProgram {
    risks: ProgramRiskRow[];
}

const TPL_BASE = '/api/v1/rsk/assessment-templates';
const PROG_BASE = '/api/v1/rsk/assessment-programs';

export const riskAssessmentProgramService = {
    async rollout(templateId: number, payload: RiskAssessmentRolloutPayload) {
        // Heavy op: one program + nested risk/control/impl score rows are inserted
        // per resolved location. Override the global 10s axios timeout so large
        // geography selections still return the success payload (and the list
        // refetches) instead of aborting mid-commit and forcing a manual refresh.
        const resp = await apiClient.post(`${TPL_BASE}/${templateId}/rollout`, payload, { timeout: 60_000 });
        return resp.data?.data;
    },

    async list(opts?: { assigned_to_me?: boolean; checker_for_me?: boolean; status?: string; search?: string }): Promise<RiskAssessmentProgram[]> {
        const params: Record<string, string> = { limit: '200' };
        if (opts?.assigned_to_me) params.assigned_to_me = 'true';
        if (opts?.checker_for_me) params.checker_for_me = 'true';
        if (opts?.status) params.status = opts.status;
        if (opts?.search) params.search = opts.search;
        const resp = await apiClient.get(PROG_BASE, { params });
        return resp.data?.data ?? [];
    },

    async get(id: number): Promise<RiskAssessmentProgramDetail> {
        const resp = await apiClient.get(`${PROG_BASE}/${id}`);
        return resp.data?.data;
    },

    async updateRiskScore(
        programId: number,
        scoreId: number,
        payload: { likelihood?: number; severity?: number; remarks?: string | null },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/risk-scores/${scoreId}`, payload);
    },

    async updateControlScore(
        programId: number,
        controlScoreId: number,
        payload: { existence_score?: number; effectiveness_score?: number; remarks?: string | null },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/control-scores/${controlScoreId}`, payload);
    },

    async updateImplScore(
        programId: number,
        implScoreId: number,
        payload: { result?: 'Pass' | 'Fail' | 'NA' | null; remarks?: string | null },
    ): Promise<void> {
        await apiClient.put(`${PROG_BASE}/${programId}/impl-scores/${implScoreId}`, payload);
    },

    async submit(programId: number): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/submit`);
    },

    async approve(programId: number): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/approve`);
    },

    async reject(programId: number, reason?: string): Promise<void> {
        await apiClient.post(`${PROG_BASE}/${programId}/reject`, { reason });
    },
};

export default riskAssessmentProgramService;
